import { Controller } from "@hotwired/stimulus"

// A merge card told from the figure being merged (review/_merge): what each figure it goes into gets, the sentence
// at the top ("splits into Frost Claw and Ember Fang") and the Merge button's words, kept as the option tiles stand.
// "Another figure it goes into…" finds a figure, offers it on every option's tile (rw-merge-dest#offer) and adds its
// card under "Into".
export default class extends Controller {
  static targets = ["subject", "card", "cards", "gets", "verb", "into", "button", "label", "query", "results", "blank"]
  static values = { lookup: String, keep: Number }

  connect() { this.count() }

  // Each figure being merged that's ticked (a pair's has no tick): its options as picked, or, with none, all of it,
  // into the kept figure (the first card).
  count() {
    const gets = new Map(this.cardTargets.map(card => [card.dataset.sum, []]))
    const keptName = this.cardTargets[0]?.dataset.sum
    this.subjectTargets.forEach(subject => {
      const tick = this.element.querySelector(`input[name='other_ids[]'][value='${subject.dataset.figureId}']`)
      if (tick && tick.type === "checkbox" && !tick.checked) return
      const picked = [...subject.querySelectorAll("input[type=radio]:checked")]
      if (picked.length === 0) gets.get(keptName)?.push(`all of ${subject.dataset.name}`)
      picked.forEach(input => { if (gets.has(input.dataset.sum)) gets.get(input.dataset.sum).push(input.dataset.option) })
    })
    this.cardTargets.forEach(card => {
      const list = gets.get(card.dataset.sum) || []
      const line = card.querySelector("[data-rw-merge-targets-target=gets]")
      if (line) line.textContent = list.length ? list.join(", ") : "nothing"
      card.classList.toggle("opacity-60", list.length === 0)
    })
    const into = this.cardTargets.filter(card => (gets.get(card.dataset.sum) || []).length)
    if (into.length === 0) return
    if (this.hasIntoTarget) {
      const names = into.map(card => Object.assign(document.createElement("b"), { className: card.dataset.nameClass, textContent: card.dataset.sum }))
      this.intoTarget.replaceChildren(...names.flatMap((name, i) => i === 0 ? [name] : [document.createTextNode(i === names.length - 1 ? " and " : ", "), name]))
    }
    if (this.hasVerbTarget) this.verbTarget.textContent = into.length > 1 ? "splits into" : this.verbTarget.dataset.was
    const words = into.length > 1 ? `Split into ${into.map(card => card.dataset.sum).join(" + ")}` : `Merge into ${into[0].dataset.sum}`
    if (this.hasLabelTarget && !this.labelTarget.textContent.startsWith("Merge as")) this.labelTarget.textContent = words
    if (this.hasButtonTarget) this.buttonTarget.dataset.turboConfirm = `${words}?`
  }

  search() {
    clearTimeout(this.timer)
    const query = this.queryTarget.value.trim()
    if (query.length < 2) { this.resultsTarget.hidden = true; return }
    this.timer = setTimeout(() => this.fetch(query), 200)
  }

  async fetch(query) {
    const url = new URL(this.lookupValue, window.location.origin)
    url.searchParams.set("q", query)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok || query !== this.queryTarget.value.trim()) return
    const taken = new Set(this.cardTargets.map(card => card.dataset.figureId).filter(Boolean))
    const figures = (await response.json()).filter(figure => figure.id !== this.keepValue && !taken.has(String(figure.id)))
    this.resultsTarget.replaceChildren(...figures.map(figure => {
      const button = Object.assign(document.createElement("button"), { type: "button", className: "cx-result" })
      button.setAttribute("role", "option")
      button.append(Object.assign(document.createElement("b"), { textContent: figure.name }))
      button.addEventListener("click", () => this.add(figure))
      return button
    }))
    this.resultsTarget.hidden = figures.length === 0
  }

  // Its card under "Into", and a place on every option's tile, picked on none.
  add(figure) {
    const card = this.blankTarget.content.firstElementChild.cloneNode(true)
    card.dataset.sum = figure.name
    card.dataset.figureId = figure.id
    card.querySelector("[data-name]").textContent = figure.name
    this.cardsTarget.append(card)
    this.element.querySelectorAll("[data-controller~='rw-merge-dest']").forEach(element => {
      this.application.getControllerForElementAndIdentifier(element, "rw-merge-dest")?.offer(figure)
    })
    this.queryTarget.value = ""
    this.resultsTarget.hidden = true
    this.count()
  }
}
